import type { QueryClient } from '@tanstack/react-query';
import { FULFILLMENT_CONTEXT_COMPACT_QUERY_KEY } from '../../hooks/queries/useFulfillmentContexts';
import { FULFILLMENT_CONTEXT_SUMMARIES_HEAVY_QUERY_KEY } from '../../components/shared/FCTXAddressForm/useFulfillmentContextSummariesQuery';
import { DOCK_LANES_QUERY_KEY } from '../../hooks/queries/useDockLanes';
import { DOCK_LANE_QUERY_KEY } from '../../hooks/queries/useDockLane';
import { DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY } from '../../hooks/queries/useDockLaneSortConfigurations';
import { DOCK_LANE_SCHEDULE_QUERY_KEY } from '../../hooks/queries/useDockLaneSchedule';
import { DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY } from '../../hooks/queries/useDockLanePostalCodeMappings';
import {
  addresses,
  carrierAccounts,
  carrierRates,
  carrierRateSets,
  carrierSettings,
  contacts,
  contracts,
  fulfillmentContexts,
  origins,
  schedules,
  tags,
  tenants,
} from '~/services/partner-management-service';

/**
 * Asset types that can be mutated
 */
type InvalidatorAssetType =
  | 'address'
  | 'carrier-account'
  | 'carrier-rate-set'
  | 'carrier-rate'
  | 'carrier-settings'
  | 'contact'
  | 'contract'
  | 'fulfillment-context'
  | 'fulfillment-contexts-compact'
  | 'fulfillment-contexts-heavy'
  | 'origin'
  | 'schedule'
  | 'tag'
  | 'tenant'
  | 'workflow'
  | 'dock-lane'
  | 'dock-lane-sort-configuration'
  | 'dock-lane-schedule'
  | 'dock-lane-postal-code-mapping';

/**
 * Action types that can be performed on assets
 */
type InvalidatorActionType = 'create' | 'update' | 'link' | 'delete';

/**
 * Mutation types supported by the query invalidation system
 * Combines asset types with action types using template literal types
 */
export type InvalidatorMutationType = `${InvalidatorAssetType}-${InvalidatorActionType}`;

type InvalidatorQueryKeyLookups = InvalidatorAssetType | `${InvalidatorAssetType}-by-id`;

/**
 * Invalidates query keys from within Redux store actions
 * This should be called from within thunks where queryClient is available via injections
 * @param queryClient The QueryClient instance from injections
 * @param invalidateQueryKeys Array of query key arrays to invalidate
 * @param sessionPartnerUrl The current partner URL from session
 */
export const invalidateQueriesWithClient = (
  queryClient: QueryClient,
  invalidateQueryKeys: string[][],
  sessionPartnerUrl: string,
) => {
  // safeguard, queryClient is lazy loaded when react mounts
  if (!queryClient) {
    return;
  }

  for (const keys of invalidateQueryKeys) {
    // hack to invalidate all queries
    if (keys.includes('invalidate-all-queries')) {
      queryClient.invalidateQueries();
      continue;
    }

    queryClient.invalidateQueries({ queryKey: [sessionPartnerUrl, ...keys], refetchType: 'active' });
  }
};

/**
 * Query key mappings for all partner management endpoints
 * Used to ensure proper invalidation when mutations occur
 * These are sourced directly from the service definitions to avoid duplication
 */
type QueryKeyLookup = {
  [K in InvalidatorQueryKeyLookups]: K extends `${string}-by-id` ? (id: string) => string[] : string[];
};
export const QUERY_KEYS: QueryKeyLookup = {
  // Carrier Rate Sets
  'carrier-rate-set': [carrierRateSets.search.queryKey],
  'carrier-rate-set-by-id': (id: string) => [carrierRateSets.get.queryKey, id],

  // Carrier Accounts
  'carrier-account': [carrierAccounts.search.queryKey],
  'carrier-account-by-id': (id: string) => [carrierAccounts.get.queryKey, id],

  // Carrier Settings
  'carrier-settings': [carrierSettings.search.queryKey],
  'carrier-settings-by-id': (id: string) => [carrierSettings.get.queryKey, id],

  // Carrier Rates
  'carrier-rate': [carrierRates.searchSummary.queryKey],
  'carrier-rate-by-id': (id: string) => [carrierRates.get.queryKey, id],

  // Contracts
  contract: [contracts.search.queryKey],
  'contract-by-id': (id: string) => [contracts.get.queryKey, id],

  // Fulfillment Contexts
  'fulfillment-context': [fulfillmentContexts.search.queryKey],
  'fulfillment-context-by-id': (id: string) => ['partner-management-service/fulfillment-contexts/id', id],

  // Addresses
  address: [addresses.search.queryKey],
  'address-by-id': (id: string) => ['partner-management-service/addresses/id', id],

  // Contacts
  contact: [contacts.search.queryKey],
  'contact-by-id': (id: string) => ['partner-management-service/contacts/id', id],

  // Origins
  origin: [origins.search.queryKey],
  'origin-by-id': (id: string) => ['partner-management-service/origins/id', id],

  // Schedules
  schedule: [schedules.search.queryKey],
  'schedule-by-id': (id: string) => ['partner-management-service/schedules/id', id],

  // Tenants
  tenant: [tenants.search.queryKey],
  'tenant-by-id': (id: string) => [tenants.get.queryKey, id],

  // Tags
  tag: [tags.search.queryKey],
  'tag-by-id': (id: string) => [tags.get.queryKey, id],

  // Workflow
  workflow: ['invalidate-all-queries'],
  'workflow-by-id': (id: string) => ['invalidate-all-queries'],

  /**
   * direct dependencies:
    partnerManagementService.fulfillmentContexts.search.fn()
    partnerManagementService.contacts.search.fn()
    partnerManagementService.addresses.search.fn()
    partnerManagementService.origins.search.fn()
    partnerManagementService.schedules.search.fn()
    partnerManagementService.tenants.search.fn()
    partnerManagementService.tags.search.fn()

   * lazy loaded:
    partnerManagementService.carrierRateSets.search
    partnerManagementService.contracts.search
   */
  'fulfillment-contexts-compact': [FULFILLMENT_CONTEXT_COMPACT_QUERY_KEY],
  'fulfillment-contexts-compact-by-id': (id: string) => [FULFILLMENT_CONTEXT_COMPACT_QUERY_KEY, id],
  'fulfillment-contexts-heavy': [FULFILLMENT_CONTEXT_SUMMARIES_HEAVY_QUERY_KEY],
  'fulfillment-contexts-heavy-by-id': (id: string) => [FULFILLMENT_CONTEXT_SUMMARIES_HEAVY_QUERY_KEY, id],

  // Dock Lanes
  'dock-lane': [DOCK_LANES_QUERY_KEY],
  'dock-lane-by-id': (id: string) => [DOCK_LANE_QUERY_KEY, id],

  // Dock Lane Sort Configurations
  'dock-lane-sort-configuration': [DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY],
  'dock-lane-sort-configuration-by-id': (id: string) => [DOCK_LANE_SORT_CONFIGURATIONS_QUERY_KEY, id],

  // Dock Lane Schedules
  'dock-lane-schedule': [DOCK_LANE_SCHEDULE_QUERY_KEY],
  'dock-lane-schedule-by-id': (id: string) => [DOCK_LANE_SCHEDULE_QUERY_KEY, id],

  // Dock Lane Postal Code Mappings
  'dock-lane-postal-code-mapping': [DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY],
  'dock-lane-postal-code-mapping-by-id': (id: string) => [DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY, id],
};

/**
 * Returns the appropriate query keys to invalidate based on the mutation type
 *
 * NOTE: The services themselves return invalidateQueryKeys arrays from their mutation methods.
 * This function provides a centralized way to get invalidation keys when calling service methods
 * from Redux actions, maintaining backward compatibility.
 *
 * In the future, consider refactoring Redux actions to use the invalidateQueryKeys returned
 * directly from service methods instead of this mapping function.
 */
export const getInvalidationKeys = (mutationType: InvalidatorMutationType, entityId?: string): string[][] => {
  const keys: string[][] = [];

  switch (mutationType) {
    case 'carrier-rate-set-create':
    case 'carrier-rate-set-update':
    case 'carrier-rate-set-link':
    case 'carrier-rate-set-delete':
      keys.push(QUERY_KEYS['carrier-rate-set']);
      if (entityId) {
        keys.push(QUERY_KEYS['carrier-rate-set-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'carrier-rate-create':
    case 'carrier-rate-update':
    case 'carrier-rate-link':
    case 'carrier-rate-delete':
      keys.push(QUERY_KEYS['carrier-rate']);
      if (entityId) {
        keys.push(QUERY_KEYS['carrier-rate-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'carrier-settings-create':
    case 'carrier-settings-update':
    case 'carrier-settings-link':
    case 'carrier-settings-delete':
      keys.push(QUERY_KEYS['carrier-settings']);
      if (entityId) {
        keys.push(QUERY_KEYS['carrier-settings-by-id'](entityId));
      }
      break;

    case 'carrier-account-create':
    case 'carrier-account-update':
    case 'carrier-account-link':
    case 'carrier-account-delete':
      keys.push(QUERY_KEYS['carrier-account']);
      if (entityId) {
        keys.push(QUERY_KEYS['carrier-account-by-id'](entityId));
      }
      break;

    case 'contract-create':
    case 'contract-update':
    case 'contract-link':
    case 'contract-delete':
      keys.push(QUERY_KEYS.contract);
      if (entityId) {
        keys.push(QUERY_KEYS['contract-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'fulfillment-context-create':
    case 'fulfillment-context-update':
    case 'fulfillment-context-link':
    case 'fulfillment-context-delete':
      keys.push(QUERY_KEYS['fulfillment-context']);
      if (entityId) {
        keys.push(QUERY_KEYS['fulfillment-context-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'address-create':
    case 'address-update':
    case 'address-link':
    case 'address-delete':
      keys.push(QUERY_KEYS.address);
      if (entityId) {
        keys.push(QUERY_KEYS['address-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'contact-create':
    case 'contact-update':
    case 'contact-link':
    case 'contact-delete':
      keys.push(QUERY_KEYS.contact);
      if (entityId) {
        keys.push(QUERY_KEYS['contact-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'origin-create':
    case 'origin-update':
    case 'origin-link':
    case 'origin-delete':
      keys.push(QUERY_KEYS.origin);
      if (entityId) {
        keys.push(QUERY_KEYS['origin-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'schedule-create':
    case 'schedule-update':
    case 'schedule-link':
    case 'schedule-delete':
      keys.push(QUERY_KEYS.schedule);
      if (entityId) {
        keys.push(QUERY_KEYS['schedule-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'tenant-create':
    case 'tenant-update':
    case 'tenant-link':
    case 'tenant-delete':
      keys.push(QUERY_KEYS.tenant);
      if (entityId) {
        keys.push(QUERY_KEYS['tenant-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'tag-create':
    case 'tag-update':
    case 'tag-link':
    case 'tag-delete':
      keys.push(QUERY_KEYS.tag);
      if (entityId) {
        keys.push(QUERY_KEYS['tag-by-id'](entityId));
      }
      keys.push(QUERY_KEYS['fulfillment-contexts-compact']);
      keys.push(QUERY_KEYS['fulfillment-contexts-heavy']);
      break;

    case 'workflow-create':
    case 'workflow-update':
    case 'workflow-link':
    case 'workflow-delete':
      keys.push(['invalidate-all-queries']); // hack to invalidate all queries
      break;

    case 'dock-lane-create':
    case 'dock-lane-update':
    case 'dock-lane-delete':
      keys.push(QUERY_KEYS['dock-lane']);
      keys.push(QUERY_KEYS['dock-lane-sort-configuration']);
      if (entityId) {
        keys.push(QUERY_KEYS['dock-lane-by-id'](entityId));
      }
      break;

    case 'dock-lane-sort-configuration-create':
    case 'dock-lane-sort-configuration-update':
    case 'dock-lane-sort-configuration-delete':
      keys.push(QUERY_KEYS['dock-lane-sort-configuration']);
      if (entityId) {
        keys.push(QUERY_KEYS['dock-lane-sort-configuration-by-id'](entityId));
      }
      break;

    case 'dock-lane-schedule-create':
    case 'dock-lane-schedule-delete':
      keys.push(QUERY_KEYS['dock-lane-schedule']);
      if (entityId) {
        keys.push(QUERY_KEYS['dock-lane-schedule-by-id'](entityId));
      }
      break;

    case 'dock-lane-postal-code-mapping-create':
    case 'dock-lane-postal-code-mapping-update':
      keys.push(QUERY_KEYS['dock-lane-postal-code-mapping']);
      break;
  }

  return keys;
};
